import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShareJobDialog } from "./ShareJobDialog";
import { firstPhoneDigits } from "../lib/phone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BalcaoManageCards = () => {
  const [balcaoAds, setBalcaoAds] = useState({ jobs: [], labor: [] });
  const [visitStats, setVisitStats] = useState(null);
  const [workOffers, setWorkOffers] = useState([]);
  const [weeklyCity, setWeeklyCity] = useState("");
  const [weeklyUrl, setWeeklyUrl] = useState(null);
  const [shareJob, setShareJob] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/balcao/visit-stats`).then((r) => setVisitStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/work-offers`).then((r) => setWorkOffers(r.data.offers || [])).catch(() => {});
    axios.get(`${API}/admin/balcao/ads`).then((r) => setBalcaoAds(r.data)).catch(() => {});
  }, []);

  const removeAd = async (type, id) => {
    if (!window.confirm("Remover este anúncio do Balcão? Esta ação não pode ser desfeita.")) return;
    try {
      await axios.delete(`${API}/admin/balcao/${type}/${id}`);
      setBalcaoAds((prev) => ({
        jobs: type === "external-jobs" ? prev.jobs.filter((j) => j.id !== id) : prev.jobs,
        labor: type === "labor-offers" ? prev.labor.filter((l) => l.id !== id) : prev.labor,
      }));
      toast.success("Anúncio removido");
    } catch {
      toast.error("Erro ao remover anúncio");
    }
  };

  const confirmViaWhatsApp = async (kind, item) => {
    try {
      const r = await axios.post(`${API}/admin/balcao/confirm-link`, { kind, item_id: item.id });
      const url = `${window.location.origin}/confirmar/${r.data.token}`;
      const isVaga = kind === "vaga";
      const msg = `Olá! 👋 Tudo bem? ${isVaga ? "Vi seu anúncio de vaga" : "Vi que você está procurando serviço"} e estou divulgando ele *gratuitamente* no Balcão de Empregos da *HeveaTalent* — uma plataforma que desenvolvi para conectar fazendas e profissionais da heveicultura.\n\n📌 *${item.title}*\n\nPara manter o anúncio no ar, só preciso de 1 resposta rápida (leva 10 segundos): ${isVaga ? "*a vaga ainda está disponível?*" : "*você ainda está disponível para trabalho?*"}\n\n👉 Toque aqui e responda: ${url}\n\nObrigado! 🌳`;
      const digits = firstPhoneDigits(item.contact);
      if (digits.length >= 10) {
        window.open(`https://wa.me/55${digits}?text=${encodeURIComponent(msg)}`, "_blank");
      } else {
        await navigator.clipboard.writeText(msg);
        toast.success("Anúncio sem telefone — mensagem com o link copiada! Cole onde preferir.");
      }
    } catch {
      toast.error("Erro ao gerar link de confirmação");
    }
  };

  const toggleOffer = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/work-offers/${id}/toggle`);
      setWorkOffers((prev) => prev.map((o) => (o.id === id ? { ...o, is_active: res.data.is_active } : o)));
      toast.success(res.data.is_active ? "Oferta reativada" : "Oferta desativada");
    } catch {
      toast.error("Erro ao alterar oferta");
    }
  };

  return (
    <>
      <Card data-testid="admin-balcao-ads-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">📢 Anúncios publicados no Balcão ({balcaoAds.jobs.length + balcaoAds.labor.length})</CardTitle>
          <p className="text-xs text-muted-foreground">
            Use 📲 Confirmar para perguntar no WhatsApp se o anúncio segue disponível — a resposta renova ou remove sozinha.
          </p>
        </CardHeader>
        <CardContent>
          {balcaoAds.jobs.length === 0 && balcaoAds.labor.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum anúncio ativo no momento.</p>
          ) : (
            <div className="space-y-2">
              {balcaoAds.jobs.map((j) => (
                <div key={j.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between" data-testid={`admin-balcao-job-${j.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex flex-wrap items-center gap-1">
                      <Badge className="bg-sky-100 text-sky-800 text-[10px]">VAGA</Badge> {j.title}
                      {j.confirmed_at && <Badge className="bg-green-100 text-green-700 text-[10px]" data-testid={`admin-job-confirmed-${j.id}`}>✅ {new Date(j.confirmed_at).toLocaleDateString("pt-BR")}</Badge>}
                    </p>
                    <p className="text-xs text-gray-600">
                      {j.city ? `${j.city} · ` : ""}{j.summary ? `${j.summary.slice(0, 90)}${j.summary.length > 90 ? "…" : ""} · ` : ""}
                      expira {new Date(j.expires_at).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      data-testid={`admin-balcao-job-confirm-${j.id}`}
                      onClick={() => confirmViaWhatsApp("vaga", j)}
                    >
                      📲 Confirmar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-sky-50 hover:bg-sky-100 text-sky-700 border-sky-200"
                      data-testid={`admin-balcao-job-share-${j.id}`}
                      onClick={() => setShareJob(j)}
                    >
                      📢 Divulgar
                    </Button>
                    <Button size="sm" variant="destructive" className="col-span-2 sm:col-span-1" data-testid={`admin-balcao-job-remove-${j.id}`} onClick={() => removeAd("external-jobs", j.id)}>
                      🗑 Remover
                    </Button>
                  </div>
                </div>
              ))}
              {balcaoAds.labor.map((l) => (
                <div key={l.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between" data-testid={`admin-balcao-labor-${l.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex flex-wrap items-center gap-1">
                      <Badge className="bg-amber-100 text-amber-800 text-[10px]">MÃO DE OBRA</Badge> {l.title}
                      {l.confirmed_at && <Badge className="bg-green-100 text-green-700 text-[10px]" data-testid={`admin-labor-confirmed-${l.id}`}>✅ {new Date(l.confirmed_at).toLocaleDateString("pt-BR")}</Badge>}
                    </p>
                    <p className="text-xs text-gray-600">
                      {l.city ? `${l.city} · ` : ""}{l.contact ? `📱 ${l.contact} · ` : ""}{l.submitter_name || ""}
                    </p>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto sm:shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 sm:flex-none bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      data-testid={`admin-balcao-labor-confirm-${l.id}`}
                      onClick={() => confirmViaWhatsApp("mao_de_obra", l)}
                    >
                      📲 Confirmar
                    </Button>
                    <Button size="sm" variant="destructive" className="flex-1 sm:flex-none" data-testid={`admin-balcao-labor-remove-${l.id}`} onClick={() => removeAd("labor-offers", l.id)}>
                      🗑 Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="admin-work-offers-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">💼 Ofertas de Trabalho de Profissionais ({workOffers.length})</CardTitle>
          <p className="text-xs text-muted-foreground">
            Criadas por profissionais cadastrados — publicam na hora e você recebe notificação. Desative o que for inadequado.
          </p>
        </CardHeader>
        <CardContent>
          {workOffers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma oferta criada por profissionais ainda.</p>
          ) : (
            <div className="space-y-2">
              {workOffers.map((o) => (
                <div key={o.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between" data-testid={`admin-work-offer-${o.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex flex-wrap items-center gap-1">
                      {o.title}
                      {o.is_example && <Badge variant="outline" className="text-[10px]">exemplo</Badge>}
                      {!o.is_active && <Badge className="bg-gray-200 text-gray-700 text-[10px]">desativada</Badge>}
                    </p>
                    <p className="text-xs text-gray-600">
                      {o.professional_name}
                      {o.professional_phone ? ` · 📱 ${o.professional_phone}` : ""}
                      {o.available_locations ? ` · ${o.available_locations}` : ""}
                    </p>
                  </div>
                  <Button size="sm" variant={o.is_active ? "outline" : "default"} data-testid={`admin-work-offer-toggle-${o.id}`} onClick={() => toggleOffer(o.id)}>
                    {o.is_active ? "Desativar" : "Reativar"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-green-300 bg-green-50">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-green-900">🔗 Link público do Balcão (divulgue nos grupos)</p>
              <p className="text-xs text-green-700 font-mono mt-0.5" data-testid="balcao-public-link">https://heveatalent.com.br/balcao</p>
              {visitStats && (
                <p className="text-xs text-green-800 mt-1" data-testid="balcao-visit-stats">
                  👁 Visitas: <b>{visitStats.total}</b> no total · <b>{visitStats.qr}</b> via QR do resumo semanal
                  <span className="text-green-600"> (últimos 7 dias: {visitStats.last7.total} · QR {visitStats.last7.qr})</span>
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                data-testid="balcao-copy-link-btn"
                onClick={() => {
                  navigator.clipboard.writeText("https://heveatalent.com.br/balcao");
                  toast.success("Link copiado! Cole nos grupos de WhatsApp.");
                }}
              >
                📋 Copiar
              </Button>
              <Button
                size="sm"
                className="bg-green-700 hover:bg-green-800"
                data-testid="balcao-open-link-btn"
                onClick={() => window.open("/balcao", "_blank")}
              >
                Abrir página
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            📲 Resumo Semanal para WhatsApp
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Imagem com preço, clima e manchete da semana — pronta para compartilhar nos grupos.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="weekly-city">Cidade (para o clima)</Label>
              <Input
                id="weekly-city"
                data-testid="weekly-card-city"
                value={weeklyCity}
                onChange={(e) => setWeeklyCity(e.target.value)}
                placeholder="Ex: Olímpia - SP"
              />
            </div>
            <Button
              data-testid="weekly-card-generate"
              className="bg-green-700 hover:bg-green-800 w-full sm:w-auto"
              onClick={async () => {
                if (weeklyCity.trim()) {
                  try {
                    const res = await axios.get(`${API}/radar/weekly-card-info?city=${encodeURIComponent(weeklyCity)}`);
                    if (res.data.weather_ok) {
                      toast.success(`Clima de ${res.data.city_label} incluído na imagem!`);
                    } else {
                      toast.error("Cidade não encontrada para o clima — confira a grafia (ex: Olímpia - SP). A imagem sairá sem a seção de clima.");
                    }
                  } catch { /* segue mesmo assim */ }
                } else {
                  toast.info("Sem cidade informada: a imagem sairá sem a seção de clima.");
                }
                setWeeklyUrl(`${API}/radar/weekly-card?city=${encodeURIComponent(weeklyCity)}&t=${Date.now()}`);
              }}
            >
              Gerar imagem
            </Button>
          </div>
          {weeklyUrl && (
            <div className="space-y-2">
              <img src={weeklyUrl} alt="Resumo semanal" className="w-full max-w-sm rounded-xl border mx-auto" data-testid="weekly-card-preview" />
              <div className="flex flex-wrap justify-center gap-2">
                <a href={weeklyUrl} download="resumo-semanal-heveatalent.png" data-testid="weekly-card-download">
                  <Button variant="outline">⬇️ Baixar imagem</Button>
                </a>
                <Button
                  data-testid="weekly-card-share"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent("🌳 Resumo da semana da borracha — tendência, clima e notícias. Acompanhe na HeveaTalent 👉 https://heveatalent.com.br")}`, "_blank")}
                >
                  💬 Abrir WhatsApp
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">
                Dica: baixe a imagem e anexe na conversa — o texto do WhatsApp já vai com o link do site.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <ShareJobDialog job={shareJob} open={!!shareJob} onOpenChange={(o) => !o && setShareJob(null)} />
    </>
  );
};
